import { bootSdk } from './core';

/**
 * IIFE entry. Reads its own <script> tag's data attributes for
 * configuration, then hands off to `bootSdk`. Every failure path is
 * swallowed — the host page's `window.onerror` must not see us.
 */
(function main(): void {
  try {
    const script = document.currentScript as HTMLScriptElement | null;
    const publishableKey = script?.dataset.publishableKey;
    const baseUrl = script?.dataset.baseUrl ?? deriveBaseUrl(script?.src);
    const debug = script?.dataset.debug === 'true';

    if (!publishableKey) {
      console.warn('[StaySignals] missing data-publishable-key; SDK disabled');
      return;
    }
    if (!baseUrl) {
      console.warn('[StaySignals] could not resolve API base URL; SDK disabled');
      return;
    }

    const start = () => {
      void bootSdk({ publishableKey, baseUrl, debug }).catch((err) => {
        if (debug) console.error('[StaySignals] boot failed', err);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  } catch (err) {
    // Last-resort swallow. Must never surface.
    try { console.warn('[StaySignals] unexpected init failure', err); } catch { /* */ }
  }
})();

/**
 * If no explicit `data-base-url` is set, infer it from the script tag's
 * src. `https://cdn.staysignals.com/staysignals.js` → `https://api.staysignals.com`.
 * The exact domain mapping is a deploy concern; the only contract is
 * "derive api host from cdn host".
 */
function deriveBaseUrl(scriptSrc: string | undefined): string | null {
  if (!scriptSrc) return null;
  try {
    const u = new URL(scriptSrc);
    if (u.hostname.startsWith('cdn.')) {
      u.hostname = 'api.' + u.hostname.slice(4);
      u.pathname = '/';
      return u.origin;
    }
    // Fallback: same origin.
    return u.origin;
  } catch {
    return null;
  }
}
